import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { EMPTY_TASK_REMOVED_MESSAGE } from 'constants/messageConstants';
import {
  PRODUCTION_CROP_RESOLVING_TOOLTIP,
  PRODUCTION_STEP_GROUP_KEY,
  TASKS_GRID_COLUMN_LABEL_AREA,
  TASKS_GRID_COLUMN_LABEL_AREA_UNIT,
  TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
  TASKS_GRID_COLUMN_LABEL_DATE,
  TASKS_GRID_COLUMN_LABEL_TASK,
  TASKS_GRID_COLUMN_LABEL_TIMING,
  TASKS_GRID_EMPTY_STATE_HINT_BUTTON_LABEL,
  TASKS_GRID_EMPTY_STATE_HINT_PREFIX,
  TASKS_GRID_EMPTY_STATE_HINT_SUFFIX,
  TASKS_GRID_EMPTY_STATE_TITLE,
  TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IInputZone } from 'interfaces/IInput';
import { IInputStep, InputStepType } from 'interfaces/IInputStep';
import {
  IInputTask,
  IInputTaskBase,
  InputTaskCategory,
  InputTaskType,
} from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import MergeClassNames from 'utils/MergeClassNames';
import { toast } from 'utils/toast';

import AddInputControl from './AddInputControl';
import InputStepDetailsModal from './InputStepDetailsModal';
import InputTableFooter from './InputTableFooter';
import ProductionTaskSteps from './ProductionTaskSteps';
import QuickAddTaskRow, { QUICK_ADD_TASK_OPTIONS } from './QuickAddTaskRow';
import {
  buildSeedLinkedReason,
  getAreaUnitOptions,
  getLinkedSeedDressItems,
  getTaskSteps,
  taskHasAnyApplicationDetail,
} from './stepCalculations';
import {
  ADD_INPUT_CATEGORY_KEY_BY_FILTER,
  ALL_STEP_CATEGORY_KEY,
  getAddCategoryLabel,
  getStepCategoryFilter,
  taskMatchesStepCategory,
} from './stepCategoryFilters';
import { isStepAllowedForTask } from './stepConfig';
import {
  buildStepFromSelection,
  keepProductionStepValues,
  ProductionCropContext,
  StepSelection,
} from './stepFactory';
import TaskDetailsSection from './TaskDetailsSection';
import TaskFilterTally from './TaskFilterTally';
import TaskInlineDetails from './TaskInlineDetails';
import {
  focusTaskAddInput,
  focusTaskDateCell,
  scrollTaskRowIntoView,
  TASK_ADD_INPUT_ATTR,
} from './taskEntryFocus';
import {
  ApplicationTaskFields,
  buildTask,
  compareTasksByEndDate,
  duplicateTask,
  TaskKind,
} from './taskFactory';
import TaskRowContextMenu from './TaskRowContextMenu';
import {
  TASK_ROW_COLUMN_COUNT,
  TASK_ROW_COLUMN_WIDTHS_REM,
} from './taskRowLayout';
import TaskStepsTable from './TaskStepsTable';
import TaskSummaryRow from './TaskSummaryRow';
import { useStepContextMenu } from './useStepContextMenu';
import { useStepReorder } from './useStepReorder';
import { useTaskDetailsVisibility } from './useTaskDetailsVisibility';

// What counts as "still working on the new task": its row and expanded steps,
// plus the pickers, menus and modals it opens (portalled outside the table).
const ENTRY_TASK_SURFACES_SELECTOR = [
  '.task-entry-row',
  '.task-entry-expanded-row',
  '.ant-popover',
  '.ant-picker-dropdown',
  '.ant-select-dropdown',
  '.ant-dropdown',
  '.ant-modal-root',
  '.row-actions-menu',
].join(', ');

const TASK_COLUMN_NOTICE_DURATION_MS = 5000;
// How long a finished or duplicated task keeps its highlight class; matches
// the 2.5s task-row-finished-fade animation in App.css.
const TASK_FINISHED_HIGHLIGHT_MS = 2500;

// Nothing the user would want kept: no date, products, timing, notes or
// application details. Area isn't counted — it's pre-filled from the plan.
const isNewTaskEmpty = (task: IInputTask, gUnits: GUnit[]): boolean =>
  !task.endDate &&
  getTaskSteps(task).length === 0 &&
  !task.timing &&
  !taskHasAnyApplicationDetail(task, gUnits);

interface TasksTableGridProps {
  selectedInput: IInput;
  draftTasks: IInputTask[];
  draftZone?: IInputZone | null;
  gUnits: GUnit[];
  productionCropContext: ProductionCropContext;
  userProfile?: PartialUserProfile;
  activeStepCategoryKey: string;
  activeTaskCategory: InputTaskCategory;
  expandedTaskKeys: string[];
  setExpandedTaskKeys: React.Dispatch<React.SetStateAction<string[]>>;
  addDraftTask: (task: IInputTask) => void;
  updateDraftTask: (task: IInputTask) => void;
  removeDraftTask: (taskId: string) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  schedulePendingNewTaskSave: (task: IInputTask) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
}

const TasksTableGrid: React.FC<TasksTableGridProps> = ({
  selectedInput,
  draftTasks,
  draftZone,
  gUnits,
  productionCropContext,
  userProfile,
  activeStepCategoryKey,
  activeTaskCategory,
  expandedTaskKeys,
  setExpandedTaskKeys,
  addDraftTask,
  updateDraftTask,
  removeDraftTask,
  schedulePendingTaskSave,
  schedulePendingNewTaskSave,
  schedulePendingTaskDelete,
}) => {
  // The task being entered from the quick-add row (OP-2534). It stays pinned
  // to the top, under the quick-add row, until it's finished — then it moves
  // into date order and is briefly highlighted.
  const [entryTaskId, setEntryTaskId] = useState<string | null>(null);
  // When an empty new task was last removed; drives the 5-second note beside
  // the Task column label. A timestamp, so a second removal restarts the timer.
  const [emptyTaskRemovedAt, setEmptyTaskRemovedAt] = useState<
    number | null
  >(null);
  const [justFinishedTaskId, setJustFinishedTaskId] = useState<string | null>(
    null
  );
  // Focus to move once the row it targets has rendered.
  const [pendingFocus, setPendingFocus] = useState<{
    taskId: string;
    target: 'date' | 'addInput' | 'row';
  } | null>(null);

  const isFiltered = activeStepCategoryKey !== ALL_STEP_CATEGORY_KEY;
  // With a filter on, tasks holding a matching product (OP-2534 AC 14).
  const matchingTaskIds = useMemo(
    () =>
      new Set(
        isFiltered
          ? draftTasks
              .filter((task) =>
                taskMatchesStepCategory(
                  getTaskSteps(task),
                  activeStepCategoryKey
                )
              )
              .map((task) => task.taskId)
          : []
      ),
    [draftTasks, activeStepCategoryKey, isFiltered]
  );

  const tasks = useMemo(() => {
    const sorted = draftTasks
      .filter((task) => task.category === activeTaskCategory)
      .sort(compareTasksByEndDate);
    // With a filter on, matching tasks come first, each group in date order.
    const ordered = isFiltered
      ? [
          ...sorted.filter((task) => matchingTaskIds.has(task.taskId)),
          ...sorted.filter((task) => !matchingTaskIds.has(task.taskId)),
        ]
      : sorted;
    const entryTask = ordered.find((task) => task.taskId === entryTaskId);
    return entryTask
      ? [entryTask, ...ordered.filter((task) => task !== entryTask)]
      : ordered;
  }, [
    draftTasks,
    activeTaskCategory,
    entryTaskId,
    isFiltered,
    matchingTaskIds,
  ]);

  // A filter is on and the task has nothing it matches: shown as a slim row,
  // never expanded (AC 15). The task being entered is never compact.
  const isCompactTask = useCallback(
    (taskId: string) =>
      isFiltered && !matchingTaskIds.has(taskId) && taskId !== entryTaskId,
    [isFiltered, matchingTaskIds, entryTaskId]
  );

  // The plan with its unsaved area/unit edits (AC 5), for new tasks and the
  // nutrient captions alike.
  const planInput = useMemo(
    () => ({ ...selectedInput, zone: draftZone ?? selectedInput.zone }),
    [selectedInput, draftZone]
  );
  const [activeSelectorTaskId, setActiveSelectorTaskId] = useState<
    string | null
  >(null);
  const [editingStepInputId, setEditingStepInputId] = useState<string | null>(
    null
  );

  const closeInputSelectors = useCallback(() => {
    setActiveSelectorTaskId(null);
    setEditingStepInputId(null);
  }, []);

  // Breaks the circular dependency between the two hooks below: useStepReorder
  // needs to close the context menu on drag-start, and useStepContextMenu needs
  // to start a reorder from its "Reorder" menu item. The ref is assigned to the
  // real function below, synchronously during render and before either hook's
  // handlers can be invoked by a user interaction.
  const closeContextMenuRef = useRef<() => void>(() => {});
  const closeContextMenu = useCallback(() => {
    closeContextMenuRef.current();
  }, []);

  const stepReorder = useStepReorder({
    tasks,
    activeStepCategoryKey,
    activeTaskCategory,
    updateDraftTask,
    schedulePendingTaskSave,
    closeContextMenu,
    closeInputSelectors,
  });
  const { startReorder, reset: resetReorder } = stepReorder;

  const stepContextMenu = useStepContextMenu({
    tasks,
    activeStepCategoryKey,
    removeDraftTask,
    schedulePendingTaskDelete,
    updateDraftTask,
    schedulePendingTaskSave,
    closeInputSelectors,
    onReorder: startReorder,
  });
  closeContextMenuRef.current = stepContextMenu.closeContextMenu;
  const {
    contextMenuState,
    contextMenuRef,
    viewDetailsStep,
    closeViewDetailsStep,
    showViewDetails,
    canReorder: contextMenuCanReorder,
    deleteDisabledReason,
    handleTaskRowContextMenu,
    openMenuFromKebab,
    handleViewDetailsFromContext,
    handleDeleteFromContext,
    handleReorderFromContext,
    reset: resetContextMenu,
  } = stepContextMenu;

  const {
    fieldVisibilityOverride,
    setFieldVisible,
    reset: resetDetailsVisibility,
  } = useTaskDetailsVisibility();

  const previousInputIdRef = useRef(selectedInput.id);

  if (selectedInput.id !== previousInputIdRef.current) {
    previousInputIdRef.current = selectedInput.id;
    setActiveSelectorTaskId(null);
    setEditingStepInputId(null);
    resetContextMenu();
    resetReorder();
    resetDetailsVisibility();
    setEntryTaskId(null);
    setJustFinishedTaskId(null);
    setPendingFocus(null);
    setEmptyTaskRemovedAt(null);
  }

  const areaUnitOptions = useMemo(() => getAreaUnitOptions(gUnits), [gUnits]);

  const commitTaskField = useCallback(
    (
      task: IInputTask,
      patch: Partial<IInputTaskBase & ApplicationTaskFields>
    ) => {
      const updatedTask = { ...task, ...patch } as IInputTask;
      if ('area' in patch || 'areaUnit' in patch) {
        updatedTask.steps = updatedTask.steps.map((step) => ({
          ...step,
          area: updatedTask.area,
          areaUnit: updatedTask.areaUnit,
        }));
      }
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    },
    [updateDraftTask, schedulePendingTaskSave]
  );

  const commitStepField = useCallback(
    (task: IInputTask, stepId: string, patch: Partial<IInputStep>) => {
      const updatedTask: IInputTask = {
        ...task,
        steps: task.steps.map((step) =>
          step.stepId === stepId ? ({ ...step, ...patch } as IInputStep) : step
        ),
      };
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    },
    [updateDraftTask, schedulePendingTaskSave]
  );

  const finishEntry = useCallback(() => {
    if (!entryTaskId) return;
    setEntryTaskId(null);
    setActiveSelectorTaskId(null);

    // A new task left with nothing entered was most likely an accidental
    // add, so finishing it cancels it rather than dropping a blank task at
    // the bottom of the list. Within the save debounce this never reaches the
    // server; after it, the delete removes the empty record.
    const entryTask = draftTasks.find((task) => task.taskId === entryTaskId);
    if (entryTask && isNewTaskEmpty(entryTask, gUnits)) {
      removeDraftTask(entryTask.taskId);
      schedulePendingTaskDelete(entryTask.taskId);
      setExpandedTaskKeys((previous) =>
        previous.filter((key) => key !== entryTask.taskId)
      );
      setEmptyTaskRemovedAt(Date.now());
      return;
    }

    setJustFinishedTaskId(entryTaskId);
    setPendingFocus({ taskId: entryTaskId, target: 'row' });
  }, [
    entryTaskId,
    draftTasks,
    gUnits,
    removeDraftTask,
    schedulePendingTaskDelete,
    setExpandedTaskKeys,
  ]);

  const handleAddTask = useCallback(
    (kind: TaskKind) => {
      if (!selectedInput) return;
      setEmptyTaskRemovedAt(null);
      // Adding the next task finishes the one being entered, so Ctrl/⌘+1/2/3
      // is also "finish and start the next one".
      finishEntry();
      // planInput carries area/unit edits not yet saved (AC 5), so a new
      // task matches what the plan header shows right now.
      const task = buildTask(
        planInput,
        kind,
        activeTaskCategory,
        userProfile
      );
      addDraftTask(task);
      schedulePendingNewTaskSave(task);
      // Expanded so its "+ Add" product button is there to move to once the
      // date is set. The Application Details card is left closed: most
      // agronomists set the date and products first.
      setExpandedTaskKeys((previous) =>
        previous.includes(task.taskId) ? previous : [...previous, task.taskId]
      );
      setEntryTaskId(task.taskId);
      setPendingFocus({ taskId: task.taskId, target: 'date' });
    },
    [
      addDraftTask,
      schedulePendingNewTaskSave,
      selectedInput,
      planInput,
      activeTaskCategory,
      userProfile,
      setExpandedTaskKeys,
      finishEntry,
    ]
  );

  useEffect(() => {
    if (!pendingFocus) return;
    // Wait a frame so the new or re-sorted row is in the DOM.
    const frame = requestAnimationFrame(() => {
      scrollTaskRowIntoView(pendingFocus.taskId);
      if (pendingFocus.target === 'date') {
        focusTaskDateCell(pendingFocus.taskId);
      } else if (pendingFocus.target === 'addInput') {
        focusTaskAddInput(pendingFocus.taskId);
      }
      setPendingFocus(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingFocus]);

  useEffect(() => {
    if (emptyTaskRemovedAt === null) return;
    const timeout = window.setTimeout(
      () => setEmptyTaskRemovedAt(null),
      TASK_COLUMN_NOTICE_DURATION_MS
    );
    return () => window.clearTimeout(timeout);
  }, [emptyTaskRemovedAt]);

  useEffect(() => {
    if (!justFinishedTaskId) return;
    const timeout = window.setTimeout(
      () => setJustFinishedTaskId(null),
      TASK_FINISHED_HIGHLIGHT_MS
    );
    return () => window.clearTimeout(timeout);
  }, [justFinishedTaskId]);

  // Once the new task's date picker closes, go straight to adding products —
  // but only while it has none, so re-editing the date later doesn't reopen
  // the picker. Deferred a tick so the click that closed the date picker
  // can't land as an outside click on the product picker and close it.
  const handleDateEditEnd = useCallback(
    (taskId: string) => {
      if (taskId !== entryTaskId) return;
      const task = tasks.find((candidate) => candidate.taskId === taskId);
      if (!task || getTaskSteps(task).length > 0) return;
      window.setTimeout(() => setActiveSelectorTaskId(taskId), 0);
    },
    [entryTaskId, tasks]
  );

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      // Escape on the new task's "+ Add" button (where focus lands after each
      // product) finishes it: Escape closes the product picker, Escape again
      // finishes the task.
      if (event.key === 'Escape' && entryTaskId && !activeSelectorTaskId) {
        const addInputOwner = (event.target as HTMLElement | null)
          ?.closest?.(`[${TASK_ADD_INPUT_ATTR}]`)
          ?.getAttribute(TASK_ADD_INPUT_ATTR);
        if (addInputOwner === entryTaskId) {
          event.preventDefault();
          finishEntry();
        }
        return;
      }

      if (!(event.ctrlKey || event.metaKey)) return;

      // Ctrl/⌘+Enter also finishes the task being entered. Skipped while the
      // product picker is open (it has its own Ctrl/⌘+Enter) and inside text
      // fields, which may use it themselves.
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement | null;
        const isTextField =
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLInputElement ||
          !!target?.isContentEditable;
        if (
          entryTaskId &&
          !activeSelectorTaskId &&
          !event.defaultPrevented &&
          !isTextField
        ) {
          event.preventDefault();
          finishEntry();
        }
        return;
      }

      const quickAddOption = QUICK_ADD_TASK_OPTIONS.find(
        (option) => option.shortcutKey === event.key
      );
      if (quickAddOption) {
        event.preventDefault();
        handleAddTask(quickAddOption.kind);
      }
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [handleAddTask, finishEntry, entryTaskId, activeSelectorTaskId]);

  // Clicking anywhere outside the new task — its row, its steps, or a picker
  // or menu it opened — finishes it, so there's no Done step to remember.
  useEffect(() => {
    if (!entryTaskId) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest || target.closest(ENTRY_TASK_SURFACES_SELECTOR)) {
        return;
      }
      finishEntry();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [entryTaskId, finishEntry]);

  // Duplicate (AC 7): the copy shares the original's date, so it sorts beside
  // it; it opens expanded and is briefly highlighted like a finished new task.
  const handleDuplicateFromContext = useCallback(() => {
    const target = contextMenuState?.target;
    closeContextMenu();
    if (!target || target.rowType !== 'task') return;
    const source = tasks.find((task) => task.taskId === target.taskId);
    if (!source) return;
    finishEntry();
    const copy = duplicateTask(source, userProfile);
    addDraftTask(copy);
    schedulePendingNewTaskSave(copy);
    setExpandedTaskKeys((previous) => [...previous, copy.taskId]);
    setJustFinishedTaskId(copy.taskId);
    setPendingFocus({ taskId: copy.taskId, target: 'row' });
  }, [
    contextMenuState,
    closeContextMenu,
    tasks,
    finishEntry,
    userProfile,
    addDraftTask,
    schedulePendingNewTaskSave,
    setExpandedTaskKeys,
  ]);

  const toggleTaskExpand = useCallback(
    (taskId: string) => {
      // Collapsing the task being entered ends its entry.
      if (taskId === entryTaskId) finishEntry();
      // Collapsing unmounts the steps wrapper, and React does not fire onBlur
      // on unmount — without this the grid would stay in reorder mode with
      // nothing focused and the arrow keys dead.
      resetReorder();
      setExpandedTaskKeys((previous) =>
        previous.includes(taskId)
          ? previous.filter((key) => key !== taskId)
          : [...previous, taskId]
      );
    },
    [resetReorder, setExpandedTaskKeys, entryTaskId, finishEntry]
  );

  const closeInputSelector = useCallback(() => {
    // While a task is being entered, closing its picker hands focus back to
    // its "+ Add" button: Enter adds another product, Ctrl/⌘+Enter finishes.
    if (activeSelectorTaskId && activeSelectorTaskId === entryTaskId) {
      setPendingFocus({ taskId: activeSelectorTaskId, target: 'addInput' });
    }
    setActiveSelectorTaskId(null);
  }, [activeSelectorTaskId, entryTaskId]);

  const handleStepSelect = useCallback(
    (task: IInputTask, selections: StepSelection[]) => {
      if (selections.length === 0) return;

      const disallowed = selections.find(
        (selection) => !isStepAllowedForTask(selection.stepType, task.taskType)
      );

      if (disallowed) {
        toast.error(TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE);
        return;
      }

      const isProductionTask = task.taskType === InputTaskType.Production;
      // A production task holds exactly one step, seeded from the resolved crop
      if (
        isProductionTask &&
        (getTaskSteps(task).length > 0 || productionCropContext.isResolving)
      ) {
        setActiveSelectorTaskId(null);
        return;
      }
      const allowedSelections = isProductionTask
        ? selections.slice(0, 1)
        : selections;

      const newSteps = allowedSelections.map((selection) =>
        buildStepFromSelection(
          task,
          selection,
          userProfile,
          productionCropContext
        )
      );

      const updatedTask: IInputTask = {
        ...task,
        steps: [...getTaskSteps(task), ...newSteps],
      };

      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
      setActiveSelectorTaskId(null);
      if (task.taskId === entryTaskId) {
        // A production task holds one step, so adding it completes the task.
        if (isProductionTask) {
          finishEntry();
        } else {
          setPendingFocus({ taskId: task.taskId, target: 'addInput' });
        }
      }
    },
    [
      updateDraftTask,
      schedulePendingTaskSave,
      userProfile,
      productionCropContext,
      entryTaskId,
      finishEntry,
    ]
  );

  const handleStepInputChange = useCallback(
    (task: IInputTask, stepId: string, selections: StepSelection[]) => {
      if (selections.length === 0) return;

      if (!isStepAllowedForTask(selections[0].stepType, task.taskType)) {
        toast.error(TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE);
        return;
      }

      // Defence-in-depth: the picker already locks the category tabs, but if
      // somehow a type change reaches here for a Seed step that a SeedDress
      // links to, reject it — replacing the step type would turn the link into
      // a dangling reference with no recoverable path.
      const existingStep = getTaskSteps(task).find((s) => s.stepId === stepId);
      if (
        existingStep?.stepType === InputStepType.Seed &&
        selections[0].stepType !== InputStepType.Seed
      ) {
        const linkedItems = getLinkedSeedDressItems(task, stepId);
        if (linkedItems.length > 0) {
          toast.error(
            buildSeedLinkedReason(linkedItems, ' to change step type')
          );
          return;
        }
      }

      const builtStep: IInputStep = {
        ...buildStepFromSelection(
          task,
          selections[0],
          userProfile,
          productionCropContext
        ),
        stepId,
      };
      const rebuiltStep = existingStep
        ? keepProductionStepValues(existingStep, builtStep)
        : builtStep;

      const updatedTask: IInputTask = {
        ...task,
        steps: task.steps.map((step) =>
          step.stepId === stepId ? rebuiltStep : step
        ),
      };

      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
      setEditingStepInputId(null);
    },
    [
      updateDraftTask,
      schedulePendingTaskSave,
      userProfile,
      productionCropContext,
    ]
  );

  const activeFilter = getStepCategoryFilter(activeStepCategoryKey);

  // The row's own "+ Add" for the filtered category (AC 16): on any collapsed
  // task the category can go in, matching or not, so a product can be added
  // without expanding. The task is highlighted where it lands, since gaining
  // a match moves it up the list.
  const renderRowAddInput = (task: IInputTask, isExpanded: boolean) => {
    if (!isFiltered || !activeFilter?.groupKey || isExpanded) return null;
    const canAdd = (activeFilter.stepTypes ?? []).some((stepType) =>
      isStepAllowedForTask(stepType, task.taskType)
    );
    const isProductionTask = task.taskType === InputTaskType.Production;
    if (!canAdd || (isProductionTask && getTaskSteps(task).length > 0)) {
      return null;
    }
    return (
      <AddInputControl
        task={task}
        label={getAddCategoryLabel(activeFilter.label)}
        addInputCategoryKey={activeFilter.groupKey}
        enableMultiSelect={!isProductionTask}
        disabledReason={
          isProductionTask && productionCropContext.isResolving
            ? PRODUCTION_CROP_RESOLVING_TOOLTIP
            : undefined
        }
        isOpen={activeSelectorTaskId === task.taskId}
        onOpenChange={(open) => {
          closeContextMenu();
          setActiveSelectorTaskId(open ? task.taskId : null);
        }}
        onClose={closeInputSelector}
        onSelect={(selections) => {
          handleStepSelect(task, selections);
          setJustFinishedTaskId(task.taskId);
          setPendingFocus({ taskId: task.taskId, target: 'row' });
        }}
      />
    );
  };

  const renderTaskSummaryRow = (task: IInputTask, index: number) => (
    <TaskSummaryRow
      task={task}
      index={index}
      isExpanded={
        expandedTaskKeys.includes(task.taskId) && !isCompactTask(task.taskId)
      }
      isMenuOpen={
        contextMenuState?.target.rowType === 'task' &&
        contextMenuState.target.taskId === task.taskId
      }
      gUnits={gUnits}
      selectedInputStartDate={selectedInput.startDate}
      activeStepCategoryKey={activeStepCategoryKey}
      areaUnitOptions={areaUnitOptions}
      isCompact={isCompactTask(task.taskId)}
      rowAddInput={renderRowAddInput(
        task,
        expandedTaskKeys.includes(task.taskId) && !isCompactTask(task.taskId)
      )}
      inlineDetails={
        expandedTaskKeys.includes(task.taskId) &&
        !isCompactTask(task.taskId) ? (
          <TaskInlineDetails
            task={task}
            gUnits={gUnits}
            fieldVisibilityOverride={fieldVisibilityOverride}
            setFieldVisible={setFieldVisible}
            commitTaskField={commitTaskField}
          />
        ) : null
      }
      commitTaskField={commitTaskField}
      onToggleExpand={toggleTaskExpand}
      onDateEditEnd={handleDateEditEnd}
      onOpenMenuFromKebab={openMenuFromKebab}
    />
  );

  const taskColumns: ColumnsType<IInputTask> = useMemo(
    () => [
      {
        // The column label doubles as the spot for a brief note about the
        // tasks below it — next to where the user was working, rather than a
        // toast in a corner.
        title: (
          <div
            className={MergeClassNames(
              'task-column-title',
              emptyTaskRemovedAt !== null && 'task-column-title-with-notice'
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="task-column-title-label flex-shrink-0">
                {TASKS_GRID_COLUMN_LABEL_TASK}
              </span>
              <span role="status" className="min-w-0 truncate">
                {emptyTaskRemovedAt !== null && (
                  <span className="task-column-notice font-normal text-charcoal">
                    {EMPTY_TASK_REMOVED_MESSAGE}
                  </span>
                )}
              </span>
            </div>
          </div>
        ),
        key: 'task',
        // No width: the Task column takes whatever the fixed columns leave.
        onHeaderCell: () => ({
          className: 'task-column-header-cell',
        }),
        onCell: () => ({ colSpan: TASK_ROW_COLUMN_COUNT }),
        render: (_value, task, index) => renderTaskSummaryRow(task, index),
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_DATE,
        key: 'date',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.date}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_TIMING,
        key: 'timing',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.timing}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_AREA,
        dataIndex: 'area',
        key: 'area',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.area}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_AREA_UNIT,
        dataIndex: ['areaUnit', 'unit'],
        key: 'areaUnit',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.areaUnit}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
        key: 'cost',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.cost}rem`,
        align: 'right',
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
    ],
    [
      activeStepCategoryKey,
      emptyTaskRemovedAt,
      expandedTaskKeys,
      toggleTaskExpand,
      areaUnitOptions,
      gUnits,
      commitTaskField,
      // renderTaskSummaryRow reads contextMenuState to keep the row's
      // three-dot button visible while its own menu is open; without this the
      // memo would hand antd a render closure that never sees the menu open.
      contextMenuState,
      openMenuFromKebab,
      handleDateEditEnd,
      // renderTaskSummaryRow reads the filter's matches and the open picker
      // (for the row's own "+ Add").
      isCompactTask,
      activeSelectorTaskId,
      handleStepSelect,
      closeInputSelector,
      productionCropContext.isResolving,
      // The expanded row's own Water rate / Target / Notes controls.
      fieldVisibilityOverride,
      setFieldVisible,
    ]
  );

  const addInputCategoryKey =
    ADD_INPUT_CATEGORY_KEY_BY_FILTER[activeStepCategoryKey];

  const renderTaskDetailsSection = useCallback(
    (task: IInputTask) => (
      <TaskDetailsSection
        task={task}
        gUnits={gUnits}
        fieldVisibilityOverride={fieldVisibilityOverride}
        commitTaskField={commitTaskField}
      />
    ),
    [gUnits, fieldVisibilityOverride, commitTaskField]
  );

  const renderInputTableFooter = useCallback(
    (task: IInputTask, hiddenCount: number) => {
      const isProductionTask = task.taskType === InputTaskType.Production;
      return (
        <>
          <TaskFilterTally
            task={task}
            planInput={planInput}
            gUnits={gUnits}
            activeStepCategoryKey={activeStepCategoryKey}
          />
          <InputTableFooter
            task={task}
            hiddenCount={hiddenCount}
            addInputCategoryKey={
              isProductionTask ? PRODUCTION_STEP_GROUP_KEY : addInputCategoryKey
            }
            enableMultiSelect={!isProductionTask}
            addInputDisabledReason={
              isProductionTask && productionCropContext.isResolving
                ? PRODUCTION_CROP_RESOLVING_TOOLTIP
                : undefined
            }
            isAddInputOpen={activeSelectorTaskId === task.taskId}
            onAddInputOpenChange={(open) => {
              closeContextMenu();
              setActiveSelectorTaskId(open ? task.taskId : null);
            }}
            onAddInputClose={closeInputSelector}
            onAddInputSelect={(selections) => handleStepSelect(task, selections)}
          />
        </>
      );
    },
    [
      planInput,
      gUnits,
      activeStepCategoryKey,
      addInputCategoryKey,
      productionCropContext.isResolving,
      activeSelectorTaskId,
      closeContextMenu,
      closeInputSelector,
      handleStepSelect,
    ]
  );

  const emptyTasksPlaceholder = (
    <div className="flex min-h-[13.75rem] items-center justify-center px-4 py-4">
      <div className="mx-auto flex w-full max-w-md flex-col items-center rounded-md bg-gray-smoke px-6 py-5 text-center shadow-sm">
        <p className="text-base font-medium text-gray-dark">
          {TASKS_GRID_EMPTY_STATE_TITLE}
        </p>
        <p className="mt-1 text-sm text-gray-dark">
          {TASKS_GRID_EMPTY_STATE_HINT_PREFIX}{' '}
          <span className="font-semibold text-secondary-dark">
            {TASKS_GRID_EMPTY_STATE_HINT_BUTTON_LABEL}
          </span>{' '}
          {TASKS_GRID_EMPTY_STATE_HINT_SUFFIX}
        </p>
      </div>
    </div>
  );

  return (
    <div className="z-10 mt-3 min-h-0 min-w-0 bg-gray-bright">
      {contextMenuState && (
        <TaskRowContextMenu
          ref={contextMenuRef}
          x={contextMenuState.x}
          y={contextMenuState.y}
          alignRight={contextMenuState.alignRight}
          showViewDetails={showViewDetails}
          showReorder={contextMenuCanReorder}
          showChangeSeedTarget={stepContextMenu.showChangeSeedTarget}
          deleteDisabledReason={deleteDisabledReason}
          onViewDetails={handleViewDetailsFromContext}
          onReorder={handleReorderFromContext}
          onChangeSeedTarget={stepContextMenu.handleChangeSeedTargetFromContext}
          onDuplicate={
            contextMenuState.target.rowType === 'task'
              ? handleDuplicateFromContext
              : undefined
          }
          onDelete={handleDeleteFromContext}
        />
      )}

      {viewDetailsStep && (
        <InputStepDetailsModal
          step={viewDetailsStep}
          onClose={closeViewDetailsStep}
        />
      )}

      <QuickAddTaskRow onAdd={handleAddTask} />

      <ConfigProvider
        theme={{
          components: {
            Table: {
              cellPaddingBlockSM: 0,
              cellPaddingInlineSM: 0,
              headerSplitColor: 'transparent',
            },
          },
        }}
      >
        <Table
          className="input-tasks-table"
          bordered
          size="small"
          pagination={false}
          tableLayout="fixed"
          rowKey={(task) => task.taskId}
          rowClassName={(task) =>
            MergeClassNames(
              // Drives the row's three-dot button reveal on hover.
              'group',
              expandedTaskKeys.includes(task.taskId) && 'font-semibold',
              expandedTaskKeys.includes(task.taskId) && 'expanded-task-row',
              contextMenuState?.target.rowType === 'task' &&
                contextMenuState.target.taskId === task.taskId &&
                'highlighted-row',
              task.taskId === entryTaskId && 'task-entry-row',
              task.taskId === justFinishedTaskId && 'task-row-just-finished',
              matchingTaskIds.has(task.taskId) && 'task-row-filter-match'
            )
          }
          onRow={(task) => ({
            onClick: () => {
              closeContextMenu();
              // A compact row has nothing to expand into under the filter.
              if (isCompactTask(task.taskId)) return;
              toggleTaskExpand(task.taskId);
            },
            onContextMenu: (event) => {
              handleTaskRowContextMenu(event, task.taskId);
            },
          })}
          columns={taskColumns}
          dataSource={tasks}
          locale={{ emptyText: emptyTasksPlaceholder }}
          expandable={{
            rowExpandable: () => true,
            showExpandColumn: false,
            indentSize: 0,
            // Compact rows stay collapsed while the filter is on; their
            // expanded state is kept for when it's cleared.
            expandedRowKeys: expandedTaskKeys.filter(
              (taskId) => !isCompactTask(taskId)
            ),
            expandedRowClassName: (task) =>
              task.taskId === entryTaskId ? 'task-entry-expanded-row' : '',
            onExpandedRowsChange: (expandedRows) => {
              setExpandedTaskKeys((previous) => [
                ...previous.filter((taskId) => isCompactTask(taskId)),
                ...(expandedRows as string[]),
              ]);
            },
            expandedRowRender: (task) =>
              task.taskType === InputTaskType.Production ? (
                <ProductionTaskSteps
                  task={task}
                  editingStepInputId={editingStepInputId}
                  setEditingStepInputId={setEditingStepInputId}
                  handleStepInputChange={handleStepInputChange}
                  commitStepField={commitStepField}
                  stepContextMenu={stepContextMenu}
                  renderTaskDetailsSection={renderTaskDetailsSection}
                  renderInputTableFooter={renderInputTableFooter}
                />
              ) : (
                <TaskStepsTable
                  task={task}
                  activeStepCategoryKey={activeStepCategoryKey}
                  gUnits={gUnits}
                  editingStepInputId={editingStepInputId}
                  setEditingStepInputId={setEditingStepInputId}
                  handleStepInputChange={handleStepInputChange}
                  commitStepField={commitStepField}
                  stepReorder={stepReorder}
                  stepContextMenu={stepContextMenu}
                  renderTaskDetailsSection={renderTaskDetailsSection}
                  renderInputTableFooter={renderInputTableFooter}
                />
              ),
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default TasksTableGrid;
